import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import i18n from '../translations/i18n'
import { useAuth } from './AuthContext'
import { apiClient } from '../services/api'

const SETTINGS_KEY = 'beehive_settings'

type Language = 'en' | 'es' | 'he' | 'ar' | 'th'
type TemperatureUnit = 'celsius' | 'fahrenheit'
type DistanceUnit = 'km' | 'miles'

interface Settings {
  language: Language
  notificationsEnabled: boolean
  temperatureUnit: TemperatureUnit
  distanceUnit: DistanceUnit
}

interface SettingsContextType {
  language: Language
  notificationsEnabled: boolean
  temperatureUnit: TemperatureUnit
  distanceUnit: DistanceUnit
  isRTL: boolean
  setLanguage: (lang: Language) => void
  toggleNotifications: () => void
  setTemperatureUnit: (unit: TemperatureUnit) => void
  setDistanceUnit: (unit: DistanceUnit) => void
}

const DEFAULT_SETTINGS: Settings = {
  language: 'en',
  notificationsEnabled: true,
  temperatureUnit: 'celsius',
  distanceUnit: 'km',
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined)

interface SettingsProviderProps {
  children: ReactNode
}

/**
 * Applies RTL direction to the document
 */
function applyDirection(lang: Language): void {
  const isRTL = lang === 'he' || lang === 'ar'
  document.documentElement.dir = isRTL ? 'rtl' : 'ltr'
  document.documentElement.lang = lang
}

/**
 * Settings provider component for admin panel
 * Manages language, notifications, and unit preferences
 */
export function SettingsProvider({ children }: SettingsProviderProps): React.JSX.Element {
  const [settings, setSettings] = useState<Settings>(() => {
    // Initialize from localStorage synchronously
    try {
      const stored = localStorage.getItem(SETTINGS_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as Settings
        // Apply direction immediately
        applyDirection(parsed.language)
        i18n.changeLanguage(parsed.language)
        return parsed
      }
    } catch (error) {
      console.error('Error loading settings:', error)
    }
    return DEFAULT_SETTINGS
  })

  const { user } = useAuth()

  /**
   * Sync settings from API when user logs in
   */
  useEffect(() => {
    if (user?.id) {
      syncSettingsFromAPI()
    }
  }, [user?.id])

  /**
   * Sync settings from API (user profile)
   */
  async function syncSettingsFromAPI(): Promise<void> {
    try {
      if (!user?.id) return
      const response = await apiClient.get(`/users/${user.id}`)
      const apiSettings = response.data?.settings
      if (apiSettings) {
        const merged: Settings = {
          language: apiSettings.language || settings.language,
          notificationsEnabled: apiSettings.notificationsEnabled ?? settings.notificationsEnabled,
          temperatureUnit: apiSettings.temperatureUnit || settings.temperatureUnit,
          distanceUnit: apiSettings.distanceUnit || settings.distanceUnit,
        }
        setSettings(merged)
        localStorage.setItem(SETTINGS_KEY, JSON.stringify(merged))
        // Apply synced settings
        if (merged.language !== i18n.language) {
          i18n.changeLanguage(merged.language)
        }
        applyDirection(merged.language)
      }
    } catch (error) {
      console.error('Error syncing settings from API:', error)
    }
  }

  /**
   * Persist settings to localStorage and API
   */
  const persistSettings = useCallback((newSettings: Settings) => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(newSettings))
      // Fire-and-forget API sync
      if (user?.id) {
        apiClient.put(`/users/${user.id}/settings`, {
          language: newSettings.language,
          notificationsEnabled: newSettings.notificationsEnabled,
          temperatureUnit: newSettings.temperatureUnit,
          distanceUnit: newSettings.distanceUnit,
        }).catch(err => console.error('Error saving settings to API:', err))
      }
    } catch (error) {
      console.error('Error persisting settings:', error)
    }
  }, [user?.id])

  /**
   * Change language with RTL handling
   */
  const setLanguage = useCallback((lang: Language) => {
    const newSettings = { ...settings, language: lang }
    setSettings(newSettings)
    persistSettings(newSettings)
    // Apply language change
    i18n.changeLanguage(lang)
    applyDirection(lang)
  }, [settings, persistSettings])

  /**
   * Toggle notification preference
   */
  const toggleNotifications = useCallback(() => {
    const newSettings = { ...settings, notificationsEnabled: !settings.notificationsEnabled }
    setSettings(newSettings)
    persistSettings(newSettings)
  }, [settings, persistSettings])

  /**
   * Set temperature unit
   */
  const setTemperatureUnit = useCallback((unit: TemperatureUnit) => {
    const newSettings = { ...settings, temperatureUnit: unit }
    setSettings(newSettings)
    persistSettings(newSettings)
  }, [settings, persistSettings])

  /**
   * Set distance unit
   */
  const setDistanceUnit = useCallback((unit: DistanceUnit) => {
    const newSettings = { ...settings, distanceUnit: unit }
    setSettings(newSettings)
    persistSettings(newSettings)
  }, [settings, persistSettings])

  const value: SettingsContextType = {
    language: settings.language,
    notificationsEnabled: settings.notificationsEnabled,
    temperatureUnit: settings.temperatureUnit,
    distanceUnit: settings.distanceUnit,
    isRTL: settings.language === 'he' || settings.language === 'ar',
    setLanguage,
    toggleNotifications,
    setTemperatureUnit,
    setDistanceUnit,
  }

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>
}

/**
 * Hook to use settings context
 * @returns Settings context
 */
export function useSettings(): SettingsContextType {
  const context = useContext(SettingsContext)
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider')
  }
  return context
}
